/**
 * Personic v3.2 Calibration Hooks
 *
 * React Query hooks for calibration management:
 * - useCalibration: Get current calibration settings
 * - useUpdateCalibration: Update calibration settings
 * - useCalibrationPresets: List available presets
 * - useApplyPreset: Apply a preset
 * - useTestCalibration: Test calibration with sample text
 * - useCalibrationDimensions: Get dimension definitions
 * - useCalibrationHistory: Get change history
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '@personic/shared'

// =============================================================================
// TYPES
// =============================================================================

export interface DimensionCalibration {
  baselineOffset: number      // -0.2 to +0.2
  extractionSensitivity: number  // 0.5 to 1.5
  importanceWeight: number    // 0.0 to 2.0
}

export interface Calibration {
  id: string
  tenant_id: string
  preset_id: string | null
  dimension_calibrations: Record<string, DimensionCalibration>
  created_at: string
  updated_at: string | null
}

export interface CalibrationPreset {
  id: string
  name: string
  description: string
  industry: string
  icon: string
  calibrations: Record<string, DimensionCalibration>
}

export interface DimensionInfo {
  id: string
  name: string
  description: string
  category: string
  categoryName: string
  default: DimensionCalibration
}

export interface DimensionCategory {
  id: string
  name: string
  description: string
  dimensions: Record<string, DimensionInfo>
}

export interface HistoryEntry {
  id: string
  change_type: 'manual' | 'preset' | 'reset'
  previous_values: Record<string, unknown>
  new_values: Record<string, unknown>
  change_reason: string | null
  changed_at: string
  changed_by_id: string | null
}

export interface TestResult {
  sample_text_preview: string
  raw_signals: Record<string, number>
  default_results: Record<string, {
    raw: number
    calibrated: number
    weight: number
    weighted_value: number
  }>
  calibrated_results: Record<string, {
    raw: number
    calibrated: number
    weight: number
    weighted_value: number
  }>
  current_preset: string | null
  dimensions_customized: number
}

// =============================================================================
// QUERY KEYS
// =============================================================================

const QUERY_KEYS = {
  calibration: ['calibration'],
  presets: ['calibration-presets'],
  dimensions: ['calibration-dimensions'],
  history: ['calibration-history'],
}

// =============================================================================
// HOOKS
// =============================================================================

/**
 * Get current calibration settings for the tenant
 */
export function useCalibration() {
  return useQuery({
    queryKey: QUERY_KEYS.calibration,
    queryFn: async () => {
      const { data } = await api.get<Calibration>('/calibration')
      return data
    },
  })
}

/**
 * Update calibration settings
 */
export function useUpdateCalibration() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      calibrations,
      changeReason,
    }: {
      calibrations: Record<string, DimensionCalibration>
      changeReason?: string
    }) => {
      const { data } = await api.put<Calibration>('/calibration', {
        dimension_calibrations: calibrations,
        change_reason: changeReason,
      })
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.calibration })
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.history })
    },
  })
}

/**
 * Get all available calibration presets
 */
export function useCalibrationPresets() {
  return useQuery({
    queryKey: QUERY_KEYS.presets,
    queryFn: async () => {
      const { data } = await api.get<CalibrationPreset[]>('/calibration/presets')
      return data
    },
  })
}

/**
 * Apply a preset to the calibration
 */
export function useApplyPreset() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      presetId,
      changeReason,
    }: {
      presetId: string
      changeReason?: string
    }) => {
      const { data } = await api.post<Calibration>('/calibration/apply-preset', {
        preset_id: presetId,
        change_reason: changeReason,
      })
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.calibration })
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.history })
    },
  })
}

/**
 * Reset calibration to default
 */
export function useResetCalibration() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async () => {
      const { data } = await api.post<Calibration>('/calibration/reset')
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.calibration })
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.history })
    },
  })
}

/**
 * Test calibration with sample text
 */
export function useTestCalibration() {
  return useMutation({
    mutationFn: async (sampleText: string) => {
      const { data } = await api.post<TestResult>('/calibration/test', {
        sample_text: sampleText,
      })
      return data
    },
  })
}

/**
 * Get dimension definitions grouped by category
 */
export function useCalibrationDimensions() {
  return useQuery({
    queryKey: QUERY_KEYS.dimensions,
    queryFn: async () => {
      const { data } = await api.get<Record<string, DimensionCategory>>(
        '/calibration/dimensions'
      )
      return data
    },
    staleTime: 1000 * 60 * 60, // 1 hour - dimensions don't change
  })
}

/**
 * Get calibration change history
 */
export function useCalibrationHistory(limit = 50, offset = 0) {
  return useQuery({
    queryKey: [...QUERY_KEYS.history, limit, offset],
    queryFn: async () => {
      const { data } = await api.get<HistoryEntry[]>('/calibration/history', {
        params: { limit, offset },
      })
      return data
    },
  })
}
